import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';

import { RouterLinkAs } from '@/lib/router-link';
import {
  Body, Box, Button, Caption, Card, DelayedSkeleton, Eyebrow, I, Icon,
  InfoCallout, PanelHeaderSkeleton, Pill, Popover, Row, Skeleton, SkeletonText,
  Stack,
} from '@leanin/ui';
import { ContentProse } from '@/components/prose';
import type { ReactionCounts, ReactionKind } from '@leanin/shared/reactions';

import { errorMessage } from '@/lib/error-message';
import { trackResourceFileDownloaded } from '@/lib/analytics/events';
import { useFormatDate } from '@/lib/format-date.client';
import { VideoEmbedFrame } from '@/components/video/index';
import { DetailPage } from '@/components/page-shell/index';
import {
  DefaultReactionDisc,
  PostActions,
  REACTION_BY_ID,
  ReactionPicker,
  ReactionStack,
  chipPadding,
} from '@/components/posts/index';
import { buildResourceHeader } from './build-resource-header';
import { CanonicalAttribution } from './CanonicalAttribution';
export interface RelatedResource {
  readonly id: string;
  readonly title: string;
}
import { AttachmentCard } from '@/components/cards/index';
import { ResourceComments } from '@/components/resources/ResourceComments';
import styles from './ResourceArticleView.module.css';
import {
  type ResourceAttachmentView,
  type ResourceDetailView,
} from '@/lib/queries/resources';
import {
  useReactToResource,
  useSaveResource,
  useUnsaveResource,
} from '@/lib/mutations/resources';
import { AuthorAttribution } from '@/components/AuthorAttribution';
import { ShareEntityButton } from '@/components/feed/ShareEntityButton';

interface Props {
  /** Adapted resource detail. `null` while the detail query is still loading
   *  — header, body card, and aside swap from skeleton stand-ins to real
   *  content when this resolves. */
  readonly resource: ResourceDetailView | null;
  /** Related resources for the aside list. `null` while the related query
   *  is still loading. */
  readonly related: ReadonlyArray<RelatedResource> | null;
  /** False for anonymous viewers on the public route — member-only actions
   *  (save, react, comment) route to sign-in or hide; reading, sharing,
   *  and PDF download remain. */
  readonly signedIn: boolean;
  readonly viewerIsAdmin: boolean;
  readonly viewerCanEdit: boolean;
}

export function ResourceArticleView({
  resource,
  related,
  signedIn,
  viewerIsAdmin,
  viewerCanEdit,
}: Props) {
  const t = useTranslations('Resources');

  // ---------- Header ----------
  // Header chrome paints from first render: when `resource` is null the
  // builder emits skeleton title / subtitle / factoid placeholders + the
  // tone-tinted cover stand-in; once the detail lands, the real header takes
  // over with mutation-aware actions.
  const builtHeader = useMemo(() => {
    if (resource === null) return buildResourceHeaderSkeleton();
    return buildResourceHeader(resource, {
      actions: (
        <ResourceHeaderActions
          resource={resource}
          signedIn={signedIn}
          viewerCanEdit={viewerCanEdit}
          viewerIsAdmin={viewerIsAdmin}
        />
      ),
    }, t);
  }, [resource, signedIn, viewerCanEdit, viewerIsAdmin, t]);

  // ---------- Aside ----------
  const aside = buildAside(resource, related);

  return (
    <DetailPage
      header={builtHeader.header}
      {...(builtHeader.cover !== undefined ? { cover: builtHeader.cover } : {})}
      {...(aside !== null ? { aside } : {})}
    >
      <DelayedSkeleton
        isLoading={resource === null}
        fallback={<ResourceArticleSkeleton />}
      >
        {resource !== null ? (
          <ResourceArticleBody resource={resource} signedIn={signedIn} />
        ) : null}
      </DelayedSkeleton>
    </DetailPage>
  );
}

// ── Header actions ────────────────────────────────────────────────────────

// Reaction-picker chip shared by the header actions and the bottom action
// bar — one popover-trigger implementation so the two can't drift.
function ResourceReactionChip({ resource }: { resource: ResourceDetailView }) {
  const t = useTranslations('Resources');
  const reactMutation = useReactToResource();
  const [pickerOpen, setPickerOpen] = useState(false);

  const reactionCounts: ReactionCounts = resource.reactionCounts;
  const viewerReaction: ReactionKind | null = resource.viewerReaction;
  const reactionTotal =
    reactionCounts.support +
    reactionCounts.insightful +
    reactionCounts.same +
    reactionCounts.helpful;
  const reactionError =
    reactMutation.error !== null ? errorMessage(reactMutation.error) : null;

  const onReact = (kind: ReactionKind) => {
    reactMutation.mutate({ id: resource.id, kind });
  };

  return (
    <Row gap="sm" align="center">
      <Popover
        open={pickerOpen}
        onOpenChange={(open) => {
          // Tapping the chip when the viewer already holds a reaction
          // toggles it off immediately (matches feed PostCard UX); the
          // picker only opens when they have no current reaction.
          if (open && viewerReaction !== null) {
            onReact(viewerReaction);
            return;
          }
          setPickerOpen(open);
        }}
        trigger={
          <Button
            variant="secondary"
            size="sm"
            style={chipPadding}
            aria-label={
              viewerReaction !== null
                ? t('detail.reactedAria', {
                    label: REACTION_BY_ID[viewerReaction].label,
                  })
                : t('detail.reactAria')
            }
          >
            <Row gap="xs" align="center">
              {reactionTotal > 0 ? (
                <ReactionStack
                  counts={reactionCounts}
                  mine={viewerReaction}
                  size={18}
                />
              ) : (
                <DefaultReactionDisc size={18} />
              )}
              <span>{reactionTotal}</span>
            </Row>
          </Button>
        }
        side="bottom"
        align="start"
      >
        <ReactionPicker
          mine={viewerReaction}
          onPick={(kind) => {
            onReact(kind);
            setPickerOpen(false);
          }}
        />
      </Popover>
      {reactionError !== null ? (
        <Body size="sm" color="destructive">
          {reactionError}
        </Body>
      ) : null}
    </Row>
  );
}

// Read-only reaction summary for anonymous viewers — reacting needs an
// account.
function ReactionSummary({ counts }: { counts: ReactionCounts }) {
  const total = counts.support + counts.insightful + counts.same + counts.helpful;
  if (total === 0) return null;
  return (
    <Row gap="xs" align="center">
      <ReactionStack counts={counts} mine={null} size={18} />
      <Caption>{total}</Caption>
    </Row>
  );
}

function ResourceHeaderActions({
  resource,
  signedIn,
  viewerCanEdit,
  viewerIsAdmin,
}: {
  resource: ResourceDetailView;
  signedIn: boolean;
  viewerCanEdit: boolean;
  viewerIsAdmin: boolean;
}) {
  const t = useTranslations('Resources');
  const saveMutation = useSaveResource();
  const unsaveMutation = useUnsaveResource();

  const toggleSaved = () => {
    if (resource.viewerSaved) {
      unsaveMutation.mutate(resource.id);
    } else {
      saveMutation.mutate({ id: resource.id, kind: resource.kind });
    }
  };

  return (
    <>
      {signedIn ? (
        <ResourceReactionChip resource={resource} />
      ) : (
        <ReactionSummary counts={resource.reactionCounts} />
      )}
      {signedIn ? (
        <Button
          variant={resource.viewerSaved ? 'primary' : 'secondary'}
          size="sm"
          icon={I.bookmark}
          onClick={toggleSaved}
        >
          {resource.viewerSaved ? t('detail.saved') : t('detail.save')}
        </Button>
      ) : null}
      <a
        href={`/api/v1/resources/${resource.id}/pdf`}
        download
        onClick={() => trackResourceFileDownloaded({ file_extension: 'pdf' })}
      >
        <Button variant="secondary" size="sm" icon={I.download}>
          {t('detail.downloadPdf')}
        </Button>
      </a>
      <ShareEntityButton
        entity={{ kind: 'resource', id: resource.id }}
        variant="primary"
        size="sm"
        showLabel
      />
      {viewerCanEdit ? (
        <Button
          href={`/admin/resources/${resource.id}/edit`}
          linkAs={RouterLinkAs}
          variant="secondary"
          size="sm"
          icon={I.edit}
        >
          {t('detail.edit')}
        </Button>
      ) : null}
      {viewerIsAdmin ? (
        <Button
          href={`/admin/resources/${resource.id}`}
          linkAs={RouterLinkAs}
          variant="secondary"
          size="sm"
          icon={I.cog}
        >
          {t('detail.manage')}
        </Button>
      ) : null}
    </>
  );
}

// ── Article body (loaded) ─────────────────────────────────────────────────

function ResourceArticleBody({
  resource,
  signedIn,
}: {
  resource: ResourceDetailView;
  signedIn: boolean;
}) {
  const t = useTranslations('Resources');
  const navigate = useNavigate();
  const saveMutation = useSaveResource();
  const unsaveMutation = useUnsaveResource();
  // Comments start expanded when the conversation already exists; a fresh
  // resource keeps the post-style closed-until-clicked behavior.
  const [commentsOpen, setCommentsOpen] = useState(resource.commentCount > 0);

  const toggleSaved = () => {
    if (resource.viewerSaved) {
      unsaveMutation.mutate(resource.id);
    } else {
      saveMutation.mutate({ id: resource.id, kind: resource.kind });
    }
  };

  // Anonymous viewers get functional-looking chips that route to the join
  // module (growth surface — existing members can switch to sign-in from
  // there) instead of silently doing nothing. No `from` return path: signup
  // ends in onboarding, not back on the referring page.
  const promptSignIn = () => void navigate({ to: '/get-started' });

  const showComments = commentsOpen && signedIn;

  return (
    <Card pad="flush" style={{ background: 'var(--card)', overflow: 'hidden' }}>
      <article>
        <Stack gap="xl" p="2xl">
          {resource.showAuthor ? <AuthorByline resource={resource} /> : null}
          <CanonicalAttribution canonicalUrl={resource.canonicalUrl} />
          <VideoEmbed resource={resource} />
          <ArticleBody source={resource.bodyMarkdown} />
          <Attachments resource={resource} />
        </Stack>
        <PostActions
          comments={resource.commentCount}
          commentsOpen={showComments}
          {...(signedIn
            ? {
                onComment: () => setCommentsOpen((o) => !o),
                onSave: toggleSaved,
                saved: resource.viewerSaved,
              }
            : {
                likes: sumReactionCounts(resource.reactionCounts),
                onLike: promptSignIn,
                onComment: promptSignIn,
              })}
          {...(signedIn
            ? { reactSlot: <ResourceReactionChip resource={resource} /> }
            : {})}
          extraSlot={
            <a
              href={`/api/v1/resources/${resource.id}/pdf`}
              download
              onClick={() =>
                trackResourceFileDownloaded({ file_extension: 'pdf' })
              }
            >
              <Button variant="ghost" size="sm" icon={I.download}>
                {t('detail.downloadPdf')}
              </Button>
            </a>
          }
          shareSlot={
            <ShareEntityButton
              entity={{ kind: 'resource', id: resource.id }}
              variant="ghost"
              size="sm"
            />
          }
        />
        {showComments ? (
          <Box py="lg" px="2xl" style={{ borderTop: '1px solid var(--border)' }}>
            <ResourceComments resourceId={resource.id} />
          </Box>
        ) : null}
      </article>
    </Card>
  );
}

function sumReactionCounts(counts: ReactionCounts): number {
  return counts.support + counts.insightful + counts.same + counts.helpful;
}

function AuthorByline({ resource }: { resource: ResourceDetailView }) {
  const fmt = useFormatDate();
  const t = useTranslations('Resources');
  const authorName = resource.author
    ? `${resource.author.firstName} ${resource.author.lastName}`.trim()
    : `Member ${resource.authorUserId.slice(0, 8)}`;
  const meta =
    resource.publishedAt !== null
      ? t('detail.updatedAt', { date: fmt.medium(resource.publishedAt) })
      : t('detail.draft');
  return (
    <AuthorAttribution
      name={authorName}
      userId={resource.author?.id ?? resource.authorUserId}
      {...(resource.author?.photoUrl ? { photo: resource.author.photoUrl } : {})}
      {...(resource.author?.jobTitle ? { role: resource.author.jobTitle } : {})}
      {...(resource.author?.company ? { company: resource.author.company } : {})}
      meta={meta}
      avatarSize={44}
    />
  );
}

// Video embeds live in the structured `attachments` field — the markdown
// sanitizer strips raw `<iframe>` from the body, so this is the only path
// for video on a resource.
function VideoEmbed({ resource }: { resource: ResourceDetailView }) {
  const video = resource.attachments.find((a) => a.kind === 'video');
  if (video === undefined) return null;
  return (
    <VideoEmbedFrame
      provider={video.provider}
      url={video.url}
      title={resource.title}
    />
  );
}

// Non-video attachments — files (PDFs, docs) + images. Renders below the
// body as a download list. Video attachments are handled by VideoEmbed.
function Attachments({ resource }: { resource: ResourceDetailView }) {
  const items = resource.attachments.filter(
    (a): a is Extract<ResourceAttachmentView, { kind: 'file' | 'image' }> =>
      a.kind === 'file' || a.kind === 'image',
  );
  if (items.length === 0) return null;
  return <AttachmentsList items={items} />;
}

function AttachmentsList({
  items,
}: {
  items: ReadonlyArray<Extract<ResourceAttachmentView, { kind: 'file' | 'image' }>>;
}) {
  const t = useTranslations('Resources');
  return (
    <Stack gap="md">
      <Eyebrow>{t('detail.attachments')}</Eyebrow>
      <Stack gap="sm">
        {items.map((att, idx) => {
          const displayName =
            att.name && att.name.trim().length > 0
              ? att.name
              : filenameFromUrl(att.url);
          return (
            <a
              key={`${att.url}-${idx}`}
              href={att.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <AttachmentCard
                title={displayName}
                kind={att.kind === 'image' ? t('attachments.image') : t('attachments.file')}
                {...(att.kind === 'image' ? { src: att.url } : {})}
              />
            </a>
          );
        })}
      </Stack>
    </Stack>
  );
}

function filenameFromUrl(url: string): string {
  try {
    const decoded = decodeURIComponent(url);
    const segs = decoded.split('/');
    return segs[segs.length - 1] ?? url;
  } catch {
    return url;
  }
}

function ArticleBody({ source }: { source: string }) {
  const t = useTranslations('Resources');
  if (source.trim().length === 0) {
    return <InfoCallout tone="neutral">{t('detail.noBody')}</InfoCallout>;
  }
  return <ContentProse source={source} startLevel={2} />;
}

// ── Skeleton sub-components ───────────────────────────────────────────────
// Co-located with the loaded variants so the rhythms stay in sync — when
// the loaded body shifts (extra row, new section), its skeleton sibling is
// inches away.

function buildResourceHeaderSkeleton(): {
  readonly header: ReactNode;
  readonly cover: undefined;
} {
  const header = (
    <Stack gap="lg">
      <Skeleton width="60%" height={32} rounded="sm" />
      <Skeleton width="80%" height={20} rounded="sm" />
      <Row gap="md" align="center" wrap>
        <Row gap="xs" align="center">
          <Skeleton width={16} height={16} rounded="sm" />
          <Skeleton width={120} height={18} rounded="sm" />
        </Row>
        <Skeleton width={3} height={3} rounded="full" />
        <Row gap="xs" align="center">
          <Skeleton width={16} height={16} rounded="sm" />
          <Skeleton width={100} height={18} rounded="sm" />
        </Row>
      </Row>
    </Stack>
  );
  return { header, cover: undefined };
}

function ResourceArticleSkeleton() {
  return (
    <Card pad="flush" style={{ overflow: 'hidden' }}>
      <Stack gap="xl" p="2xl">
        <Row gap="md" align="center">
          <Skeleton
            width={44}
            height={44}
            rounded="full"
            style={{ flexShrink: 0 }}
          />
          <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
            <Skeleton width="35%" height={14} rounded="sm" />
            <Skeleton width="25%" height={12} rounded="sm" />
          </Stack>
        </Row>
        <Stack gap="lg">
          <SkeletonText lines={4} size="lg" />
          <SkeletonText lines={3} size="lg" />
          <SkeletonText lines={5} size="lg" />
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Aside ─────────────────────────────────────────────────────────────────

function buildAside(
  resource: ResourceDetailView | null,
  related: ReadonlyArray<RelatedResource> | null,
): ReactNode {
  if (resource === null) {
    return (
      <Stack
        gap="lg"
        style={{ position: 'sticky', top: 'var(--space-lg)', alignSelf: 'flex-start' }}
      >
        <Card pad="compact">
          <Stack gap="md">
            <PanelHeaderSkeleton />
            <Stack gap="sm">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} width="75%" height={14} rounded="sm" />
              ))}
            </Stack>
          </Stack>
        </Card>
        <Card pad="compact">
          <Stack gap="md">
            <PanelHeaderSkeleton />
            <Row gap="xs" wrap>
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} width={72} height={26} rounded="full" />
              ))}
            </Row>
          </Stack>
        </Card>
        <RelatedSkeleton />
      </Stack>
    );
  }

  const hasTopics = resource.tags.length > 0;
  const relatedLoading = related === null;
  const hasRelated = related !== null && related.length > 0;

  if (!hasTopics && !hasRelated && !relatedLoading) {
    return null;
  }

  return (
    <Stack
      gap="lg"
      style={{ position: 'sticky', top: 'var(--space-lg)', alignSelf: 'flex-start' }}
    >
      {hasTopics ? <TopicsSection tags={resource.tags} /> : null}
      {relatedLoading ? (
        <RelatedSkeleton />
      ) : hasRelated ? (
        <RelatedSection related={related} />
      ) : null}
    </Stack>
  );
}

function TopicsSection({
  tags,
}: {
  tags: ReadonlyArray<{ slug: string; label: string }>;
}) {
  const t = useTranslations('Resources');
  return (
    <Card pad="compact">
      <Stack gap="md">
        <Eyebrow tone="accent">{t('detail.topics')}</Eyebrow>
        <Row gap="sm" rowGap="sm" wrap>
          {tags.map((tag) => (
            <Pill
              key={tag.slug}
              tone="burgundy"
              size="sm"
              href={`/resources?${new URLSearchParams({ tags: tag.slug }).toString()}`}
              linkAs={RouterLinkAs}
            >
              {tag.label}
            </Pill>
          ))}
        </Row>
      </Stack>
    </Card>
  );
}

function RelatedSkeleton() {
  return (
    <Card pad="compact">
      <Stack gap="md">
        <PanelHeaderSkeleton />
        <Stack gap="md">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} width="85%" height={14} rounded="sm" />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

function RelatedSection({ related }: { related: ReadonlyArray<RelatedResource> }) {
  const t = useTranslations('Resources');
  return (
    <Card pad="compact">
      <Stack gap="md">
        <Eyebrow tone="accent">{t('detail.related')}</Eyebrow>
        <Stack gap="md">
          {related.map((r) => (
            <RouterLinkAs
              key={r.id}
              href={`/resources/${r.id}`}
              aria-label={t('list.card.openResource', { title: r.title })}
            >
              <Row gap="md" align="center" justify="space-between">
                {/* eslint-disable-next-line @leanin/ui/typography-component-required -- same semibold title exception as the preview cards */}
                <h3 className={styles.relatedTitle}>{r.title}</h3>
                <Icon size={14} color="var(--muted-foreground)">{I.arrow}</Icon>
              </Row>
            </RouterLinkAs>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

